import { trackCtaClick } from '#utils/utils';
import {
  Badge,
  Box,
  Button,
  Container,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import React from 'react';
import { FaArrowUp, FaChartLine, FaCheckCircle } from 'react-icons/fa/index.js';
import {
  MdAttachMoney,
  MdDashboard,
  MdGroup,
  MdPsychology,
  MdReceipt,
  MdTask,
  MdTrendingDown,
  MdTrendingUp,
} from 'react-icons/md/index.js';

const MotionBox = motion(Box);

const DASHBOARD_TILES = [
  { label: 'Monthly Revenue', value: '$142,800', trend: '+12%', up: true, icon: MdAttachMoney, color: 'green' },
  { label: 'Open Projects', value: '14', trend: '3 due this week', up: false, icon: MdTask, color: 'blue' },
  { label: 'Support Tickets', value: '7', trend: '-40% vs last month', up: true, icon: MdDashboard, color: 'purple' },
  { label: 'Outstanding Invoices', value: '$28,400', trend: '4 overdue', up: false, icon: MdReceipt, color: 'orange' },
  { label: 'Team Utilization', value: '87%', trend: '+5% this week', up: true, icon: MdGroup, color: 'teal' },
  { label: 'AI Weekly Summary', value: 'Ready', trend: 'Generated Sunday', up: true, icon: MdPsychology, color: 'pink' },
];

const DATA_SOURCES = [
  'QuickBooks / Xero',
  'HubSpot / Salesforce',
  'Stripe / Square',
  'Harvest / Toggl',
  'Zendesk / Freshdesk',
  'Microsoft 365 / Google Workspace',
  'Asana / Monday / Jira',
  'Custom databases and APIs',
];

const AI_FEATURES = [
  { title: 'Weekly Business Digest', desc: 'Every Sunday, an AI-generated summary of the week: what changed, what needs attention, and what to watch next week.' },
  { title: 'Anomaly Detection', desc: 'Revenue drops, ticket spikes, or utilization dips — the system flags them before they become problems.' },
  { title: 'Trend Forecasting', desc: 'Based on your historical data, the dashboard projects where key metrics are heading over the next 30, 60, and 90 days.' },
  { title: 'Natural Language Queries', desc: 'Ask your dashboard a question in plain English: "What was our best month this year?" and get an instant answer.' },
];

const DELIVERABLES = [
  { step: '01', title: 'Data Source Audit', desc: 'We identify every system that holds business data and determine which sources matter most to your decision-making.' },
  { step: '02', title: 'Dashboard Architecture', desc: 'We design the layout and metric hierarchy before building a single connection — you approve it before we touch a system.' },
  { step: '03', title: 'Integration Build', desc: 'We connect each data source, normalize the data, and build the real-time pipeline that keeps the dashboard current.' },
  { step: '04', title: 'AI Configuration', desc: 'We configure the AI summary layer — triggers, thresholds, and weekly digest formatting tailored to your priorities.' },
  { step: '05', title: '30-Day Support', desc: 'We monitor, tune, and support the dashboard through your first full month of live operation.' },
];

const ExecutiveDashboardPage = () => {
  const pageBg = useColorModeValue('gray.50', 'gray.950');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.900', 'white');
  const dashBg = useColorModeValue('gray.900', 'gray.900');

  return (
    <Box bg={pageBg}>
      {/* Hero */}
      <Box
        bgGradient="linear(to-br, green.600, teal.400)"
        py={{ base: 20, md: 32 }}
        position="relative"
        overflow="hidden"
      >
        <Box position="absolute" top="-80px" right="-80px" w="400px" h="400px" borderRadius="full" bg="whiteAlpha.100" pointerEvents="none" />
        <Box position="absolute" bottom="-60px" left="-60px" w="300px" h="300px" borderRadius="full" bg="whiteAlpha.50" pointerEvents="none" />

        <Container maxW="container.xl">
          <Stack direction={{ base: 'column', lg: 'row' }} spacing={12} align="center">
            <MotionBox
              flex="1"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 } as any}
            >
              <Badge bg="whiteAlpha.200" color="white" fontSize="sm" px={4} py={1} borderRadius="full" mb={4} display="inline-block">
                Solution 3 of 3
              </Badge>
              <Heading
                as="h1"
                fontSize={{ base: '3xl', md: '5xl' }}
                fontWeight="extrabold"
                color="white"
                lineHeight="1.1"
                letterSpacing="-0.03em"
                mb={6}
              >
                Executive
                <br />
                Dashboard
              </Heading>
              <Text fontSize="xl" color="whiteAlpha.800" mb={8} maxW="lg">
                Your entire business on one screen. Revenue, projects, tickets,
                invoices, team utilization, and AI-generated summaries —
                updated in real time. Stop running your business from spreadsheets.
              </Text>
              <HStack spacing={4} flexWrap="wrap">
                <Button
                  size="lg" bg="white" color="green.700" fontWeight="bold" px={8}
                  as="a" href="/contact-us?interest=executive-dashboard"
                  onClick={() => trackCtaClick({ cta_label: 'Show Me My Dashboard', cta_location: 'ExecDashboard Hero', destination_url: '/contact-us?interest=executive-dashboard' })}
                  _hover={{ bg: 'gray.50', transform: 'translateY(-2px)', boxShadow: 'xl' }}
                  transition="all 0.2s"
                >
                  Show Me My Dashboard
                </Button>
                <Button
                  size="lg" variant="outline" borderColor="whiteAlpha.600" color="white" fontWeight="bold" px={8}
                  as="a" href="/pricing"
                  _hover={{ bg: 'whiteAlpha.100', transform: 'translateY(-2px)' }}
                  transition="all 0.2s"
                >
                  See Pricing
                </Button>
              </HStack>
            </MotionBox>

            {/* Mock Dashboard */}
            <MotionBox
              flex="0 0 auto" w={{ base: 'full', lg: '480px' }}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 } as any}
            >
              <Box
                bg={dashBg}
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="2xl"
                border="1px solid"
                borderColor="gray.700"
              >
                {/* Dashboard header bar */}
                <Box bg="gray.800" px={5} py={3} borderBottom="1px solid" borderColor="gray.700">
                  <HStack spacing={3} justify="space-between">
                    <HStack spacing={2}>
                      <Icon as={MdDashboard} color="green.400" boxSize={4} />
                      <Text fontSize="sm" fontWeight="bold" color="white">TenK Executive Dashboard</Text>
                    </HStack>
                    <Badge colorScheme="green" fontSize="2xs" borderRadius="full">Live</Badge>
                  </HStack>
                </Box>
                {/* Tiles */}
                <Box p={4}>
                  <SimpleGrid columns={2} spacing={3}>
                    {DASHBOARD_TILES.map((tile, i) => (
                      <MotionBox
                        key={tile.label}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4 + i * 0.1 } as any}
                        bg="gray.800"
                        border="1px solid"
                        borderColor="gray.700"
                        borderRadius="xl"
                        p={4}
                        _hover={{ borderColor: `${tile.color}.500` }}
                        style={{ transition: 'all 0.2s ease' }}
                      >
                        <HStack justify="space-between" mb={1}>
                          <Icon as={tile.icon} color={`${tile.color}.400`} boxSize={4} />
                          <Icon
                            as={tile.up ? MdTrendingUp : MdTrendingDown}
                            color={tile.up ? 'green.400' : 'orange.400'}
                            boxSize={3}
                          />
                        </HStack>
                        <Text fontSize="lg" fontWeight="extrabold" color="white" lineHeight="1">
                          {tile.value}
                        </Text>
                        <Text fontSize="2xs" color="gray.400" mt={0.5}>{tile.label}</Text>
                        <Text fontSize="2xs" color={tile.up ? 'green.400' : 'orange.400'} mt={1} fontWeight="medium">
                          {tile.trend}
                        </Text>
                      </MotionBox>
                    ))}
                  </SimpleGrid>
                </Box>
              </Box>
            </MotionBox>
          </Stack>
        </Container>
      </Box>

      {/* What's on the dashboard */}
      <Box py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }} fontWeight="extrabold" color={headingColor} letterSpacing="-0.02em">
              Everything you need to run your business — in one view.
            </Heading>
            <Text fontSize="lg" color={textColor} maxW="2xl">
              No more toggling between five tools to piece together a picture.
              Your dashboard connects the dots automatically.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {[
              { icon: MdAttachMoney, label: 'Revenue & Cash Flow', desc: 'Total revenue, MRR, outstanding invoices, and cash position — updated from your accounting system in real time.', color: 'green' },
              { icon: FaChartLine, label: 'Sales Pipeline', desc: 'Deals by stage, close probability, and projected close dates — pulled directly from your CRM.', color: 'blue' },
              { icon: MdTask, label: 'Projects & Deadlines', desc: 'Active projects, completion percentage, upcoming milestones, and at-risk items flagged automatically.', color: 'purple' },
              { icon: MdDashboard, label: 'Support Tickets', desc: 'Open tickets, resolution time, satisfaction scores, and volume trends from your helpdesk system.', color: 'orange' },
              { icon: MdGroup, label: 'Team Utilization', desc: 'Billable vs. non-billable hours, capacity by team member, and overtime risk — from your time tracking tool.', color: 'teal' },
              { icon: MdPsychology, label: 'AI Weekly Digest', desc: 'An AI-generated summary every Sunday: what happened, what needs attention, and what to watch.', color: 'pink' },
            ].map((item, i) => (
              <MotionBox
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 } as any}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="xl"
                p={7}
                _hover={{ borderColor: `${item.color}.300`, transform: 'translateY(-4px)' }}
                style={{ transition: 'all 0.3s ease' }}
              >
                <Icon as={item.icon} boxSize={8} color={`${item.color}.500`} mb={4} />
                <Heading as="h3" fontSize="md" fontWeight="bold" color={headingColor} mb={2}>{item.label}</Heading>
                <Text fontSize="sm" color={textColor} lineHeight="1.7">{item.desc}</Text>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Data Sources */}
      <Box bg={useColorModeValue('green.50', 'green.950')} py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <Stack direction={{ base: 'column', md: 'row' }} spacing={16} align="center">
            <Box flex="1">
              <Badge colorScheme="green" mb={4} fontSize="sm" px={3} py={1} borderRadius="full">Integrations</Badge>
              <Heading as="h2" fontSize={{ base: 'xl', md: '3xl' }} fontWeight="extrabold" color={headingColor} mb={4} letterSpacing="-0.02em">
                Connects to the tools you already use.
              </Heading>
              <Text fontSize="lg" color={textColor} mb={6}>
                We don't replace your existing software. We connect it.
                If it has an API, we can pull data from it.
              </Text>
              <SimpleGrid columns={2} spacing={3}>
                {DATA_SOURCES.map((src) => (
                  <HStack key={src} spacing={2}>
                    <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                    <Text fontSize="sm" color={headingColor} fontWeight="medium">{src}</Text>
                  </HStack>
                ))}
              </SimpleGrid>
            </Box>
            <Box flex="1">
              <Badge colorScheme="pink" mb={4} fontSize="sm" px={3} py={1} borderRadius="full">AI Layer</Badge>
              <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="extrabold" color={headingColor} mb={6} letterSpacing="-0.02em">
                The AI layer that turns data into decisions.
              </Heading>
              <VStack spacing={5} align="stretch">
                {AI_FEATURES.map((feat, i) => (
                  <MotionBox
                    key={feat.title}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 } as any}
                  >
                    <HStack align="flex-start" spacing={3}>
                      <Icon as={FaArrowUp} color="green.500" boxSize={4} mt={0.5} flexShrink={0} />
                      <Box>
                        <Text fontWeight="bold" color={headingColor} fontSize="sm">{feat.title}</Text>
                        <Text fontSize="sm" color={textColor} mt={1} lineHeight="1.6">{feat.desc}</Text>
                      </Box>
                    </HStack>
                  </MotionBox>
                ))}
              </VStack>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Deliverables */}
      <Box bg={useColorModeValue('gray.900', 'gray.950')} py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Badge colorScheme="green" fontSize="sm" px={4} py={1} borderRadius="full">The Process</Badge>
            <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }} fontWeight="extrabold" color="white" letterSpacing="-0.02em">
              From disconnected tools to unified visibility.
            </Heading>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={6}>
            {DELIVERABLES.map((d, i) => (
              <MotionBox
                key={d.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 } as any}
                bg="gray.800"
                border="1px solid"
                borderColor="gray.700"
                borderRadius="xl"
                p={6}
                _hover={{ borderColor: 'green.400', transform: 'translateY(-4px)' }}
                style={{ transition: 'all 0.3s ease' }}
              >
                <Text fontSize="3xl" fontWeight="extrabold" color="green.400" lineHeight="1" mb={3} fontFamily="mono">{d.step}</Text>
                <Heading as="h3" fontSize="md" fontWeight="bold" color="white" mb={2}>{d.title}</Heading>
                <Text fontSize="sm" color="gray.400" lineHeight="1.6">{d.desc}</Text>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Case Study */}
      <Box py={{ base: 16, md: 20 }}>
        <Container maxW="container.md">
          <Box
            bg={cardBg} border="1px solid" borderColor={borderColor}
            borderRadius="2xl" p={{ base: 8, md: 12 }} boxShadow="xl" textAlign="center"
          >
            <Badge colorScheme="green" mb={4} fontSize="sm" px={3} py={1} borderRadius="full">Real Result</Badge>
            <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="extrabold" color={headingColor} mb={4}>
              PHFAMOESCEF reduced total IT and telecom expenses by 27% after we
              consolidated 4 vendors into 2 and built unified visibility across all three facilities.
            </Heading>
            <Text fontSize="md" color={textColor} mb={8} maxW="xl" mx="auto">
              They went from multiple invoices, siloed systems, and reactive IT
              decisions to a single pane of glass across communications, connectivity,
              and operations.
            </Text>
            <Button
              as="a"
              href="/case-studies/phfamoescef"
              colorScheme="green"
              size="lg"
              px={8}
              _hover={{ transform: 'translateX(2px)' }}
              transition="all 0.2s"
            >
              Read the Full Case Study
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box bgGradient="linear(to-r, green.600, teal.400)" py={{ base: 16, md: 20 }}>
        <Container maxW="container.md" textAlign="center">
          <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} fontWeight="extrabold" color="white" mb={4} letterSpacing="-0.02em">
            Ready to see your business in one place?
          </Heading>
          <Text fontSize="lg" color="whiteAlpha.800" mb={8}>
            Book a free 30-minute discovery call. We'll identify your key data
            sources and design your dashboard before we build a single connection.
          </Text>
          <HStack spacing={4} justify="center" flexWrap="wrap">
            <Button
              size="lg" bg="white" color="green.700" fontWeight="bold" px={10}
              as="a" href="/contact-us?interest=executive-dashboard&package=launch"
              onClick={() => trackCtaClick({ cta_label: 'Show Me My Dashboard CTA', cta_location: 'ExecDashboard CTA', destination_url: '/contact-us?interest=executive-dashboard' })}
              _hover={{ bg: 'gray.50', transform: 'translateY(-2px)', boxShadow: 'xl' }}
              transition="all 0.2s"
            >
              Show Me My Dashboard
            </Button>
            <Button
              size="lg" variant="outline" borderColor="whiteAlpha.600" color="white" px={8}
              as="a" href="/pricing"
              _hover={{ bg: 'whiteAlpha.100' }}
            >
              Starting at $3,000
            </Button>
          </HStack>
        </Container>
      </Box>
    </Box>
  );
};

export default ExecutiveDashboardPage;
